import { useState, useEffect } from "react";
import {
  Checkbox,
  Dialog,
  Grid,
  OutlinedInput,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import StructureFields from "components/_refactor/fields/structure";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import MonthYearField from "components/_refactor/fields/month-year";
import TableFaixasBonificacao from "./table-faixas-bonificacao";
import InputBonusMeta from "./input-bonus-meta";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import getMonthByNumber from "utils/get-month-by-number";
import MaskAndValid from "utils/maskAndValid";

const MONTH_INDEX = new Date().getMonth() + 1;
const YEAR = new Date().getFullYear();

export default function GoalsDialog({ open, onClose, goal }) {
  const [structureFilters, setStructureFilters] = useState({});
  const [structureFiltersLoading, setStructureFiltersLoading] = useState({});
  const [month, setMonth] = useState({
    value: MONTH_INDEX,
    label: getMonthByNumber(MONTH_INDEX),
  });
  const [year, setYear] = useState({ value: YEAR, label: String(YEAR) });

  const [searching, setSearching] = useState(false);

  const [bonusType, setBonusType] = useState("V");
  const [bonusValue, setBonusValue] = useState("");
  const [percentFrom, setPercentFrom] = useState("");
  const [percentTo, setPercentTo] = useState("");
  const [isMaxGoal, setIsMaxGoal] = useState(false);
  const [bonusRanges, setBonusRanges] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const [openConfirmation, setOpenConfirmation] = useState(false);

  const lastRange =
    bonusRanges.length > 0 ? bonusRanges[bonusRanges.length - 1] : null;
  const lastIsMax = lastRange?.isMaxGoal;
  const disableInputs = lastIsMax && editIndex === null;
  const disableMax = isMaxGoal || disableInputs;

  const snackbar = useSnackbar();

  useEffect(() => {
    if (lastRange && !lastIsMax && editIndex === null) {
      setPercentFrom((parseFloat(lastRange.max) + 0.1).toFixed(1));
    }
  }, [lastRange, lastIsMax, editIndex]);

  useEffect(() => {
    if (goal && goal.bonificacoes && Array.isArray(goal.bonificacoes)) {
      setBonusRanges(
        goal.bonificacoes.map((b) => ({
          bonusValue: b.TipoValor === "P" ? b.BonusMeta * 100 : b.BonusMeta,
          bonusType: b.TipoValor === "P" ? "P" : "V",
          min: b.Inicial,
          max: b.Final,
          isMaxGoal: b.Final === 0,
        }))
      );
    }
  }, [goal]);

  function handleAddBonusGoal() {
    if (!bonusValue || !percentFrom || (!isMaxGoal && !percentTo)) {
      snackbar.showMessage("Preencha todos os campos.", {
        severity: "error",
      });
      return;
    }

    if (
      isMaxGoal &&
      bonusRanges.some((r, i) => r.isMaxGoal && i !== editIndex)
    ) {
      snackbar.showMessage("Já existe uma faixa de meta máxima.", {
        severity: "error",
      });
      return;
    }

    if (!isMaxGoal && parseFloat(percentFrom) >= parseFloat(percentTo)) {
      snackbar.showMessage('O valor "Entre" deve ser menor que o valor "e".', {
        severity: "error",
      });
      return;
    }

    const rawBonus = String(bonusValue).replace(/\./g, "").replace(",", ".");
    const numBonus = parseFloat(rawBonus) || 0;
    const newItem = {
      bonusValue: numBonus,
      bonusType,
      min: parseFloat(String(percentFrom).replace(",", ".")) || 0,
      max: isMaxGoal ? 0 : parseFloat(String(percentTo).replace(",", ".")) || 0,
      isMaxGoal,
    };
    if (editIndex !== null) {
      const updated = [...bonusRanges];
      updated[editIndex] = newItem;
      setBonusRanges(updated);
      setEditIndex(null);
    } else {
      setBonusRanges([...bonusRanges, newItem]);
    }
    setBonusValue("");
    setPercentFrom("");
    setPercentTo("");
    setIsMaxGoal(false);
  }

  function handleEditBonus(index) {
    const item = bonusRanges[index];
    setBonusType(item.bonusType || "V");
    setBonusValue(
      item.bonusType === "P"
        ? MaskAndValid.maskDecimal(item.bonusValue, 3)
        : MaskAndValid.maskPrice(item.bonusValue)
    );
    setPercentFrom(item.min.toString());
    setPercentTo(item.max.toString());
    setIsMaxGoal(item.isMaxGoal || false);
    setEditIndex(index);
  }

  function handleDeleteBonus(index) {
    setBonusRanges(bonusRanges.filter((_, i) => i !== index));
    if (editIndex === index) {
      setEditIndex(null);
      setBonusValue("");
      setPercentFrom("");
      setPercentTo("");
      setIsMaxGoal(false);
    }
  }

  const handleCreate = async ({ override = false }) => {
    if (
      !goal &&
      (!structureFilters.idGrupoEconomico ||
        !structureFilters.idLoja ||
        !month ||
        !year)
    ) {
      snackbar.showMessage(
        "Preencha grupo econômico, loja e período (mês/ano).",
        {
          severity: "error",
        }
      );
      return;
    }
    if (bonusRanges.length === 0) {
      snackbar.showMessage("Adicione pelo menos uma meta.", {
        severity: "error",
      });
      return;
    }
    if (!bonusRanges.some((r) => r.isMaxGoal)) {
      snackbar.showMessage("Inclua uma faixa de meta máxima.", {
        severity: "error",
      });
      return;
    }

    setSearching(true);
    const body = {
      idGrupoEconomico: goal
        ? goal.idGrupoEconomico
        : structureFilters.idGrupoEconomico,
      idLoja: goal ? goal.idLoja : structureFilters.idLoja,
      mes: goal ? goal.mes : month.value,
      ano: goal ? goal.ano : year.value,
      bonificacoes: bonusRanges.map((range) => ({
        BonusMeta: range.bonusValue,
        TipoValor: range.bonusType || "V",
        Inicial: range.min,
        Final: range.max,
      })),
      ...(override ? { forcarAtualizacao: true } : {}),
    };

    try {
      await Api.post("bonus-creation", "goals", body, true);
      snackbar.showMessage("Bonificações criadas com sucesso.", {
        severity: "success",
      });
      onClose(true);
    } catch (error) {
      if (error.includes("Já existem metas cadastradas para")) {
        setOpenConfirmation(true);
        return;
      }
      snackbar.showMessage("Erro ao criar as bonificações: " + error.message, {
        severity: "error",
      });
    } finally {
      setSearching(false);
    }
  };

  const handleOverride = () => {
    setOpenConfirmation(false);
    handleCreate({ override: true });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      maxWidth={"lg"}
    >
      <DialogTitle>
        <Typography>{goal ? "Editar" : "Criar"} Bonificações</Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          {goal ? (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Loja</Typography>
                <Typography variant="caption">{goal.loja}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Período</Typography>
                <Typography variant="caption">
                  {getMonthByNumber(goal.mes)}/{String(goal.ano)}
                </Typography>
              </Grid>
            </>
          ) : (
            <>
              <StructureFields
                onStructureFieldsChange={setStructureFilters}
                disableAll={searching || goal}
                onStructureFieldsLoad={setStructureFiltersLoading}
                requiredFields={["economicGroup", "store"]}
                hideFields={
                  goal
                    ? ["brand", "channel", "representative", "store"]
                    : ["brand", "channel", "representative"]
                }
                defaultEconomicGroup={goal?.idGrupoEconomico}
                singleStoreId={goal?.idLoja}
                showSingleStore={goal}
              />
              <MonthYearField
                month={month}
                handleMonthChange={setMonth}
                year={year}
                handleYearChange={setYear}
                disabled={searching || !!goal}
                required={true}
                displayEmpty={false}
                direction="future"
                yearsCount={2}
              />
            </>
          )}
        </Grid>

        <Grid
          container
          style={{
            marginTop: "1rem",
            padding: "10px",
            border: "1px solid #d9dfe6",
            borderRadius: "20px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography variant="caption">Bônus Meta</Typography>
            <InputBonusMeta
              value={bonusValue}
              onChange={(e) => setBonusValue(e.target.value)}
              type={bonusType}
              onTypeChange={setBonusType}
              disabled={disableInputs}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography variant="caption">Entre</Typography>
            <OutlinedInput
              value={percentFrom}
              onChange={(e) =>
                setPercentFrom(e.target.value.replace(/[^0-9.,]/g, ""))
              }
              disabled={disableInputs || bonusRanges.length > 0}
              endAdornment={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#e9ecef",
                    position: "absolute",
                    right: 0,
                    height: "100%",
                    padding: 8,
                  }}
                >
                  <span style={{ color: "#8898aa" }}>%</span>
                </div>
              }
              style={{ width: 100 }}
              inputProps={{ style: { textAlign: "right", paddingRight: 20 } }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography variant="caption">e</Typography>
            <OutlinedInput
              value={isMaxGoal ? "" : percentTo}
              onChange={(e) =>
                setPercentTo(e.target.value.replace(/[^0-9.,]/g, ""))
              }
              disabled={disableMax}
              endAdornment={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#e9ecef",
                    position: "absolute",
                    right: 0,
                    height: "100%",
                    padding: 8,
                  }}
                >
                  <span style={{ color: "#8898aa" }}>%</span>
                </div>
              }
              style={{ width: 100, opacity: disableMax ? 0.5 : 1 }}
              inputProps={{ style: { textAlign: "right", paddingRight: 20 } }}
            />
            <Checkbox
              checked={isMaxGoal}
              onChange={(e) => setIsMaxGoal(e.target.checked)}
              color="primary"
              disabled={disableInputs}
            />
            <Typography variant="caption" style={{ color: "#90A4AE" }}>
              Meta Máxima
            </Typography>
          </div>
          {editIndex !== null ? (
            <IconButton color="primary" onClick={handleAddBonusGoal}>
              <CheckIcon />
            </IconButton>
          ) : (
            <IconButton color="primary" onClick={handleAddBonusGoal}>
              <AddIcon />
            </IconButton>
          )}
        </Grid>

        <TableFaixasBonificacao
          data={bonusRanges}
          onEdit={handleEditBonus}
          onDelete={handleDeleteBonus}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancelar
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          style={{ width: "150px" }}
          disabled={searching || structureFiltersLoading.length > 0}
        >
          Salvar
        </Button>
      </DialogActions>
      <Dialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        maxWidth={"sm"}
      >
        <DialogContent>
          <Typography>
            {
              "Já existem metas cadastradas para essa loja no período selecionado."
            }
          </Typography>
          <Typography>{"Deseja sobrescrever as metas existentes?"}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmation(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleOverride} variant="contained">
            Sobrescrever
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
